import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';
import SEO from '../components/SEO';

interface Step {
  number: string;
  title: string;
  description: string;
  features: string[];
  icon: React.ReactNode;
}

const steps: Step[] = [
  {
    number: '01',
    title: 'Send JSON, Supaserver creates your database automatically',
    description: 'Supaserver uses a schema-less JSON database. You don\'t need to design tables or plan your data model in advance. Supaserver adapts to your data as you build.',
    features: [
      'Store any JSON data',
      'No predefined schema or migrations',
      'Collections are created automatically on the first request',
      'Change data structure anytime without breaking your API',
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
      </svg>
    ),
  },
  {
    number: '02',
    title: 'Your REST API is generated instantly',
    description: 'As soon as data exists, Supaserver automatically generates a full REST API. All endpoints are ready to use immediately.',
    features: [
      'CRUD endpoints out of the box',
      'GET, POST, PUT, PATCH, DELETE support',
      'Automatic routing — no backend code required',
      'Query nested JSON fields',
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Filter, sort, and paginate your data',
    description: 'Supaserver supports powerful querying features without extra code. Build complex data queries directly through the API — ideal for real-world applications.',
    features: [
      'Advanced filtering (MongoDB-style filters)',
      'Sorting by any field',
      'Pagination for large datasets',
      'Flexible query parameters',
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
      </svg>
    ),
  },
  {
    number: '04',
    title: 'Secure access with built-in authentication',
    description: 'Supaserver includes authentication and access control by default. Each request can be securely scoped to a specific user or application — without implementing auth logic yourself.',
    features: [
      'API keys for secure access',
      'JWT-based authentication',
      'Google OAuth login',
      'User-based data isolation',
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    ),
  },
  {
    number: '05',
    title: 'Test and iterate in real time',
    description: 'Supaserver provides a visual API interface to work faster. Prototype, test, and debug your API in real time — without external tools.',
    features: [
      'Send requests directly from the dashboard',
      'View responses instantly',
      'Edit JSON with syntax highlighting',
      'Organize requests by collections',
      'Work with multiple tabs in parallel',
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
];

const problemIcons = {
  code: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  ),
  database: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
    </svg>
  ),
  server: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
    </svg>
  ),
  security: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  maintenance: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
};

const problems = [
  { icon: problemIcons.code, text: 'Writing server-side code' },
  { icon: problemIcons.database, text: 'Designing database schemas and migrations' },
  { icon: problemIcons.server, text: 'Setting up hosting, deployment, and DevOps' },
  { icon: problemIcons.security, text: 'Managing authentication and security' },
  { icon: problemIcons.maintenance, text: 'Maintaining infrastructure over time' },
];

const useCaseIcons = {
  startup: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  ai: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  tools: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
    </svg>
  ),
  hackathon: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  dashboard: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
};

const useCases = [
  { icon: useCaseIcons.startup, title: 'Startups building MVPs', description: 'Ship faster without backend overhead' },
  { icon: useCaseIcons.ai, title: 'AI tools and internal AI services', description: 'Perfect for evolving data structures' },
  { icon: useCaseIcons.tools, title: 'Internal tools and admin panels', description: 'Quick dashboards and management interfaces' },
  { icon: useCaseIcons.hackathon, title: 'Hackathons and rapid prototypes', description: 'Build and validate ideas in hours' },
  { icon: useCaseIcons.dashboard, title: 'Dashboards and data-driven apps', description: 'Real-time data visualization' },
];

const differences = [
  'Does not require SQL or schemas',
  'Does not require server configuration',
  'Does not require deployment pipelines',
  'Does not require backend engineers to get started',
];

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO 
        title="How Supaserver Works — Create REST API in Seconds Without Code"
        description="Learn how Supaserver works: send JSON, get an auto-generated REST API, secure and query your data instantly. No servers, no schema, no DevOps required."
        canonical="https://supaserver.app/how-it-works"
      />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-20 px-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-orange-500/5 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500/20 rounded-2xl mb-8">
              <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              How{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">
                Supaserver
              </span>{' '}
              Works
            </h1>
            
            <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8">
              Create a REST API in Seconds Without Backend Code
            </p>

            {/* Quick overview */}
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20">
                <span className="text-orange-400">①</span>
                <span className="text-gray-200 text-sm">Send JSON</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20">
                <span className="text-orange-400">②</span>
                <span className="text-gray-200 text-sm">Get REST API</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20">
                <span className="text-orange-400">③</span>
                <span className="text-gray-200 text-sm">Query Instantly</span>
              </div>
            </div>
          </div>
        </section>

        {/* What is Supaserver */}
        <section className="py-20 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                What is Supaserver?
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Supaserver is a <span className="font-semibold text-orange-600">no-code Backend as a Service (BaaS)</span> that lets you create a fully functional REST API in seconds, without writing server-side code, setting up databases, or managing infrastructure.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <p className="text-lg text-gray-700 leading-relaxed text-center">
                Instead of building and deploying a backend manually, you simply <span className="font-semibold">define the data you need</span> — and Supaserver instantly provides a <span className="font-semibold text-orange-600">secure, scalable API</span> to store, query, and manage it.
              </p>
            </div>
          </div>
        </section>

        {/* The Problem Section */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-full text-sm font-medium mb-4">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                The Problem
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Traditional Backend Development is Complex
              </h2>
              <p className="text-lg text-gray-600">
                Even simple APIs can take <span className="font-semibold">days or weeks</span> to set up.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {problems.map((problem, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-4 p-5 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-gray-500 border border-gray-200 flex-shrink-0">
                    {problem.icon}
                  </div>
                  <span className="text-gray-700 font-medium">{problem.text}</span>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <div className="inline-flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl border border-orange-200">
                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-lg font-semibold text-orange-700">
                  Supaserver removes this complexity entirely.
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* How it works overview */}
        <section className="py-20 px-4 bg-gray-900">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              How Supaserver Works
            </h2>
            <p className="text-lg text-gray-400 mb-12">
              Three simple steps to your API
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-orange-400 font-bold text-xl">1</span>
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">Send your data as JSON</h3>
                <p className="text-gray-400 text-sm">No schema definition required</p>
              </div>
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-orange-400 font-bold text-xl">2</span>
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">Get an auto-generated REST API</h3>
                <p className="text-gray-400 text-sm">Instant CRUD endpoints</p>
              </div>
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-orange-400 font-bold text-xl">3</span>
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">Secure and query your data instantly</h3>
                <p className="text-gray-400 text-sm">Built-in authentication</p>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap justify-center gap-4 text-gray-400">
              <span className="px-4 py-2 bg-white/5 rounded-full border border-white/10">No servers</span>
              <span className="px-4 py-2 bg-white/5 rounded-full border border-white/10">No schema</span>
              <span className="px-4 py-2 bg-white/5 rounded-full border border-white/10">No DevOps</span>
            </div>
          </div>
        </section>

        {/* Step by Step Section */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Step by Step Guide
              </h2>
              <p className="text-lg text-gray-600">
                Everything you need to know about building with Supaserver
              </p>
            </div>

            <div className="space-y-8">
              {steps.map((step, index) => (
                <div 
                  key={index}
                  className="group relative bg-gray-50 rounded-3xl p-8 md:p-10 border border-gray-100 hover:border-orange-200 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Step number and icon */}
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-105 transition-transform">
                        {step.icon}
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-sm font-bold text-orange-500 bg-orange-100 px-3 py-1 rounded-full">
                          Step {step.number}
                        </span>
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {step.description}
                      </p>
                      
                      {/* Features grid */}
                      <div className="grid sm:grid-cols-2 gap-3">
                        {step.features.map((feature, featureIndex) => (
                          <div 
                            key={featureIndex}
                            className="flex items-start gap-3 p-3 bg-white rounded-xl"
                          >
                            <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Perfect For Section */}
        <section className="py-20 px-4 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Perfect For
              </h2>
              <p className="text-lg text-gray-600">
                MVPs and prototypes • AI tools with evolving data • Internal tools and dashboards
              </p>
            </div>
          </div>
        </section>

        {/* How Supaserver is Different */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-full text-sm font-medium mb-4">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                The Difference
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Unlike Traditional BaaS Platforms
              </h2>
              <p className="text-lg text-gray-600">
                Supaserver is designed for <span className="font-semibold">speed, flexibility, and simplicity</span>.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {differences.map((diff, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-4 p-5 bg-green-50 rounded-xl border border-green-100"
                >
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700 font-medium">{diff}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who is Supaserver For */}
        <section className="py-20 px-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Who is Supaserver For?
              </h2>
              <p className="text-lg text-gray-400">
                If your goal is to ship quickly without backend overhead, Supaserver fits naturally into your workflow.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {useCases.map((useCase, index) => (
                <div 
                  key={index}
                  className="p-6 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 hover:border-orange-500/30 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center text-orange-400 mb-4 border border-orange-500/20">
                    {useCase.icon}
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{useCase.title}</h3>
                  <p className="text-gray-400 text-sm">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Get started in seconds
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Create your first REST API without writing backend code.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/api-request"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-white rounded-full font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                style={{ backgroundColor: '#f97316' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ea580c'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f97316'}
              >
                <span>Create an API in seconds</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              No credit card required
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

